import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { MESSAGES } from "@/modules/shared/constants/messages";
import { useToast } from "@/modules/shared/hooks/use-toast";
import type { MutationRequest } from "@/modules/shared/types/mutation-request.type";
import type { ResponseAPI } from "@/modules/shared/types/response-api.type";

import {
	type TOrderReplicateService,
	orderReplicateService,
} from "../services/order-replicate.service";
import { useLogger } from "@/modules/shared/hooks/use-logger.hook";

export const useReplicateOrderMutation = ({
	callback,
	resolveRequest,
}: MutationRequest) => {
	const { toast } = useToast();
	const { log } = useLogger();
	const {
		mutateAsync: replicateOrderRequest,
		isSuccess: successReplicateOrder,
		isLoading: isLoadingReplicateOrder,
	} = useMutation({
		mutationFn: async (data: TOrderReplicateService) =>
			orderReplicateService(data),
		onSuccess: (data, variables) => {
			if (data?.status && data.status >= 200 && data.status < 300) {
				log("success", "order-replication", "Pedido replicado com sucesso", {
					response: data,
					payload: variables.payload,
				});
			} else {
				log("error", "order-replication", "Erro ao replicar pedido", {
					response: data,
					payload: variables.payload,
				});
			}
			resolveRequest({
				status: data.status,
				showToastInfo: true,
				message: data.message,
				showToastSuccess: true,
				callback,
			});
		},
		onError: (error: AxiosError<ResponseAPI>, variables) => {
			log("error", "order-replication", "Erro ao replicar pedido", {
				error,
				payload: variables.payload,
			});
			if (
				error.response?.data?.status > 400 &&
				error.response?.data?.status < 500 &&
				error.response?.data?.message
			) {
				toast.info({
					title: error.response.data.message,
				});
			} else {
				toast.error({
					title: MESSAGES.ERROR_TITLE,
					description: MESSAGES.ERROR_DESCRIPTION,
					action: {
						label: MESSAGES.RELOAD,
						onClick: () => {
							window.location.reload();
						},
					},
				});
			}
		},
	});

	return {
		replicateOrderRequest,
		successReplicateOrder,
		isLoadingReplicateOrder,
	};
};
